import * as React from 'react';

import { useIntegrationsQuery } from '../../graphql/integration/queryIntegrations';
import { usePortfolioParentContentsQuery } from '../../graphql/content/queryPortfolioContents';
import { usePortfoliosQuery } from '../../graphql/portfolio/queryPortfolios';
import { useTeam } from '../../context/TeamProvider';

import { useCan } from '../../Auth/rbac/useCan';
import { PLANS } from '../../entitlements/plans';
import { useStarterPlanOverride, usePlan } from '../../entitlements/useEntitlement';
import {
  CHECKLIST_STORAGE_KEY,
  type ChecklistState,
  isRetired,
  parseChecklistState,
  serializeChecklistState,
  withRetired,
} from './retirement';
import { type Checklist, starterChecklist } from './steps';
import { connectedCount } from './starterChannelRows';
import { useRefetchProvidersOnChange } from '../../hooks/useRefetchProvidersOnChange';
import { useRolePreviewOverride } from '../../Auth/rbac/useRole';
import useUserStore from '../../store/user';

export type StarterChecklistApi = {
  enabled: boolean;
  /** The portfolio the "add content" step links into, once one exists. */
  portfolioId: number | null;
  checklist: Checklist;
  retire: () => void;
};

/**
 * Fast enough that a first sync completing while the user reads the card is noticed in the
 * same visit, slow enough that a whole day of a stalled sync costs ~2,880 requests rather
 * than one a second. Only ever armed while step 3 is genuinely pending.
 */
export const CHECKLIST_POLL_MS = 30_000;

/**
 * A whole re-read, never a cached module value: another tab can retire the card, and every
 * write below re-reads before it merges. `parseChecklistState` already degrades
 * malformed JSON to `{}`; the try/catch is for the ACCESS, which throws in a locked profile.
 */
function readChecklistState(): ChecklistState {
  try {
    return parseChecklistState(window.localStorage.getItem(CHECKLIST_STORAGE_KEY));
  } catch {
    return {};
  }
}

function persistChecklistState(state: ChecklistState): void {
  try {
    window.localStorage.setItem(CHECKLIST_STORAGE_KEY, serializeChecklistState(state));
  } catch {
    // A full or blocked store must not take the app shell down with it: the in-memory state
    // still applies for this session, the latch simply does not survive a reload.
  }
}

/**
 * The Nexoya Starter setup checklist, gated and resolved. EVERY hook is called
 * unconditionally — gating is `skip` on the two queries plus `enabled` for the card's early
 * return. Branching before the queries would be a conditional hook.
 */
export function useStarterChecklist(): StarterChecklistApi {
  const { teamId } = useTeam();
  const plan = usePlan();
  const planOverride = useStarterPlanOverride();
  const canCreatePortfolio = useCan('portfolio.create');
  const isSupportUser = useUserStore((s) => s.isSupportUser);
  // A ROLE preview suspends the support bypass too, not just a plan preview: without this the
  // checklist was the one agency surface that stayed hidden under `?preview_role=admin`.
  const rolePreview = useRolePreviewOverride();

  const [stored, setStored] = React.useState<ChecklistState>(readChecklistState);
  // Read inside `skipPollAttempt`, never rendered — see the poll options below.
  const pollArmed = React.useRef(false);
  const lastGoodIntegrations = React.useRef<readonly any[] | null>(null);
  const lastGoodTeam = React.useRef(teamId);

  const enabled =
    // `null` means UNRESOLVED, never "not Starter".
    plan === PLANS.STARTER &&
    Boolean(teamId) &&
    // READONLY cannot create a portfolio, so step 2 would be a dead end for that seat.
    canCreatePortfolio &&
    // Support staff are not onboarding the customer's team, but an ACTIVE preview override —
    // either axis — suspends that bypass exactly as `useEntitlement.ts:91` does.
    !(isSupportUser && planOverride === null && rolePreview === null) &&
    !isRetired(stored, teamId);

  const integrationsQuery = useIntegrationsQuery({
    withUser: true,
    withConnection: true,
    skip: !enabled,
    // `withUser`/`withConnection` are @include VARIABLES, not field arguments, and
    // `Integration` has no `keyFields`, so every variable set writes the same
    // `ROOT_QUERY.integrations` slot — a cached write here overwrites what Settings ›
    // Integrations reads.
    fetchPolicy: 'no-cache',
    // `'all'` lands a partial failure on `error` instead of throwing; the branch below then
    // reads it as UNRESOLVED rather than as an empty roster.
    errorPolicy: 'all',
    context: { skipGlobalErrorToast: true },
    // CONSTANT, never derived. `pollInterval` is part of Apollo's watchQueryOptions equality
    // check, so changing it reobserves DURING render and refetches immediately — and this
    // query's own result decides whether to poll, so a derived value is a feedback loop that
    // fires a request per round trip whenever consecutive answers disagree.
    pollInterval: CHECKLIST_POLL_MS,
    // Each tick is gated here instead. Armed until the checklist is complete: nothing
    // invalidates these queries when the user acts elsewhere in the app, and this is also the
    // only retry an errored query gets. Background tabs are skipped outright.
    skipPollAttempt: () => !pollArmed.current || document.visibilityState !== 'visible',
  });

  const portfoliosQuery = usePortfoliosQuery({
    first: 1,
    skip: !enabled,
    errorPolicy: 'all',
    context: { skipGlobalErrorToast: true },
  });

  const firstPortfolio = enabled && !portfoliosQuery.error ? portfoliosQuery.data?.portfolios?.edges?.[0]?.node : null;
  const portfolioId: number | null = firstPortfolio?.portfolioId ?? null;

  // `first: 1` is a "does this portfolio hold anything" probe, not a list. The connection's own
  // args key the cache entry, so it never collides with the Content tab's own page.
  const contentsQuery = usePortfolioParentContentsQuery({
    portfolioId: portfolioId ?? 0,
    first: 1,
    skip: !enabled || portfolioId === null,
  });

  // `null` on either input is what `steps.ts` reads as unresolved. An errored query is NOT an
  // empty answer, and there is no cached first paint to fall back on — without this a fixed
  // panel flashes "0 of 3" on every hard load, team switch and portfolio mutation.
  const freshIntegrations = enabled && !integrationsQuery.error ? (integrationsQuery.data?.integrations ?? null) : null;
  // A blip must not erase an answer we already had. `no-cache` means Apollo keeps nothing, so
  // the last good payload is held here and the card stays up while the poll retries.
  const integrations = freshIntegrations ?? (enabled ? lastGoodIntegrations.current : null);
  const portfolios = enabled && !portfoliosQuery.error ? portfoliosQuery.data?.portfolios : null;
  const hasPortfolio = portfolios ? (portfolios.edges?.length ?? 0) > 0 : null;

  // No portfolio is a SETTLED answer for step 3, not an unresolved one: there is nothing to
  // hold content yet, so the step is genuinely not done rather than unknown.
  const contentEdges = contentsQuery.data?.portfolioV2?.portfolioParentContents?.edges;
  const hasContent =
    hasPortfolio === false ? false : contentsQuery.error ? null : contentEdges ? contentEdges.length > 0 : null;

  const checklist = starterChecklist({ integrations, hasPortfolio, hasContent });

  // Read by `skipPollAttempt` at tick time, so a ref rather than state: writing state here
  // would be the option change this design exists to avoid.
  React.useEffect(() => {
    pollArmed.current = enabled && !checklist.allComplete;
  }, [enabled, checklist.allComplete]);

  // One effect, because two would race on mount: a separate team-reset would run AFTER the
  // write and clear the payload it had just stored. A stale roster from the previous team is
  // worse than none at all, so the reset has to come first, in the same pass.
  React.useEffect(() => {
    if (lastGoodTeam.current !== teamId) {
      lastGoodIntegrations.current = null;
      lastGoodTeam.current = teamId;
    }
    if (freshIntegrations) lastGoodIntegrations.current = freshIntegrations;
  }, [freshIntegrations, teamId]);

  // Logos and channel filters come from `ProvidersProvider`'s cached `providers` query; both
  // integration cards do this, and without it the cache stays stale until a full reload. A
  // NUMBER, not the rows array — `checklist.rows` is a fresh array every render.
  useRefetchProvidersOnChange(connectedCount(checklist.rows));

  const retire = React.useCallback(() => {
    const next = withRetired(readChecklistState(), teamId, new Date().toISOString());
    persistChecklistState(next);
    setStored(next);
  }, [teamId]);

  return {
    enabled,
    portfolioId,
    checklist,
    retire,
  };
}
